import { trackGuidelineDownload } from '@utils/gtm'
import React, { useEffect, useRef, useState } from 'react'
import { HiOutlineDownload, HiArrowUp } from 'react-icons/hi'
import { scrollToElementSmoothly } from '@utils/utilities'

type supportedTypes = 'ai' | 'psd' | 'pdf' | 'zip'
export interface IGuidelineAsset {
  assetId: string
  assetType: supportedTypes
  assetUrl: string
}

function DownloadAssets({
  guidelineAssets,
}: {
  guidelineAssets: IGuidelineAsset[]
}) {
  const guidelineAssetsSorted = guidelineAssets.sort(function (a, b) {
    if (a.assetType < b.assetType) {
      return -1
    }
    if (a.assetType > b.assetType) {
      return 1
    }
    return 0
  })

  const assetTypeBackgroundMapper: { [type in supportedTypes]: string } = {
    ai: 'bg-[#FF9502]',
    psd: 'bg-[#68CBDE]',
    pdf: 'bg-[#FD566F]',
    zip: 'bg-[#9271F0]',
  }

  return (
    <div className="flex">
      {guidelineAssetsSorted?.map((guidelineAsset) => {
        return (
          guidelineAsset.assetType &&
          guidelineAsset.assetUrl && (
            <a
              className={`flex grow items-stretch mr-2 last:mr-0 focus-visible:outline-core-rose rounded-lg font-bold min-w-[56px] min-h-[56px] ${
                assetTypeBackgroundMapper[guidelineAsset.assetType]
              }`}
              target="_blank"
              rel="noreferrer"
              download
              href={guidelineAsset.assetUrl}
              key={guidelineAsset.assetUrl}
              onClick={() => {
                trackGuidelineDownload({
                  event: 'designer-download-template',
                  designTemplateFormat: guidelineAsset.assetType,
                })
              }}
            >
              <div
                className={`flex flex-col grow uppercase text-white text-xl justify-center items-center`}
              >
                <div>{guidelineAsset.assetType}</div>
                <div>
                  <HiOutlineDownload className="h-6" />
                </div>
              </div>
            </a>
          )
        )
      })}
    </div>
  )
}

function DesktopGuidelineDownload({
  guidelineAssets,
  selectedVariantOptions,
}: {
  guidelineAssets: IGuidelineAsset[]
  selectedVariantOptions: string[]
}) {
  return (
    <div className="flex items-center font-mori">
      <DownloadAssets guidelineAssets={guidelineAssets} />
      <div className="ml-7">
        {selectedVariantOptions?.map((variantOption, index) => (
          <div className="text-lg text-black-80" key={index}>
            {variantOption}
          </div>
        ))}
      </div>
    </div>
  )
}

function MobileGuidelineDownload({
  guidelineAssets,
  selectedVariantOptions,
}: {
  guidelineAssets: IGuidelineAsset[]
  selectedVariantOptions: string[]
}) {
  const [expanded, setExpanded] = useState(false)
  const assetsButtonsRef = useRef(null)

  const onClick = () => {
    setExpanded(!expanded)
  }

  useEffect(() => {
    if (!expanded) {
      return
    }
    const heightOfFooter = 100
    const bottomViewportOffsetY =
      window.scrollY + window.screen.availHeight - heightOfFooter
    const bottomButtonsOffsetY =
      assetsButtonsRef.current.offsetTop + assetsButtonsRef.current.offsetHeight
    if (bottomViewportOffsetY > bottomButtonsOffsetY) {
      return
    }
    scrollToElementSmoothly(assetsButtonsRef.current, {
      offset:
        bottomViewportOffsetY -
        window.scrollY -
        assetsButtonsRef.current.offsetHeight,
    })
  }, [expanded])

  return (
    <div>
      <div className="flex items-center font-mori">
        <div
          className="cursor-pointer flex flex-row min-h-[52px] px-12 text-2xl rounded-lg border-2 text-acai items-center bg-core-white border-black-20"
          onClick={onClick}
        >
          <span className="mr-5">Download</span>
          {expanded ? (
            <HiArrowUp className="h-6 font-bold" />
          ) : (
            <HiOutlineDownload className="h-6 font-bold" />
          )}
        </div>
        <div className="ml-5 grow">
          {selectedVariantOptions?.map((variantOption, index) => (
            <div className="text-lg text-black-80" key={index}>
              {variantOption}
            </div>
          ))}
        </div>
      </div>
      <div
        className={`mt-6 ${!expanded ? 'hidden' : ''}`}
        ref={assetsButtonsRef}
      >
        <DownloadAssets guidelineAssets={guidelineAssets} />
      </div>
    </div>
  )
}

export function GuidelineDownload({
  guidelineAssets,
  selectedVariantOptions,
  className,
}: {
  guidelineAssets: IGuidelineAsset[]
  selectedVariantOptions: string[]
  className?: string
}) {
  if (guidelineAssets?.length > 0) {
    return (
      <div className={className}>
        <h2 className="mb-6 text-2xl text-boulder font-mori">
          Download our guidelines
        </h2>
        <div className="block md:hidden">
          <MobileGuidelineDownload
            guidelineAssets={guidelineAssets}
            selectedVariantOptions={selectedVariantOptions}
          />
        </div>
        <div className="hidden md:block">
          <DesktopGuidelineDownload
            guidelineAssets={guidelineAssets}
            selectedVariantOptions={selectedVariantOptions}
          />
        </div>
      </div>
    )
  } else {
    return null
  }
}
